import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useApp } from '@/components/AppLayout';
import {
  creditMemoApi,
  type CreditMemoAllocationInfo,
  type OutstandingInvoice,
  type OutstandingQuote,
} from '@/lib/api';
import { getInvoiceStatusConfig } from '@/lib/invoiceStatus';
import { getQuoteStatusConfig } from '@/lib/quoteStatus';
import { formatAmount, getCurrencySymbol } from '@/lib/currency';
import { CreditCard, User, ArrowLeft, Receipt, FileText } from 'lucide-react';
import { format } from 'date-fns';

// Currency input component for allocation amounts
function CurrencyInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^0-9.]/g, '');
    // Only allow one decimal point and two decimal places
    const parts = input.split('.');
    if (parts.length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    onChange(input);
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        {getCurrencySymbol()}
      </span>
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className="pl-7 text-right h-9"
        placeholder="0.00"
      />
    </div>
  );
}

function ApplyCreditMemoContent() {
  const { nav, navigate } = useBusinessNav();
  const { id } = useParams();
  const { currentBusiness } = useApp();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allocationInfo, setAllocationInfo] = useState<CreditMemoAllocationInfo | null>(null);

  // Selection and amounts
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [invoiceAmounts, setInvoiceAmounts] = useState<Record<string, string>>({});
  const [selectedQuotes, setSelectedQuotes] = useState<Set<string>>(new Set());
  const [quoteAmounts, setQuoteAmounts] = useState<Record<string, string>>({});

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Load data
  useEffect(() => {
    if (!id) {
      setError('No credit memo specified');
      setLoading(false);
      return;
    }

    creditMemoApi.getAllocationInfo(id).then((response) => {
      if (response.data) {
        setAllocationInfo(response.data);
        setLoading(false);
      } else if (response.error) {
        setError(response.error);
        setLoading(false);
      }
    });
  }, [id]);

  // Calculate total allocation amount
  const totalAllocationAmount = useMemo(() => {
    let total = 0;
    selectedInvoices.forEach((invoiceId) => {
      const amount = parseFloat(invoiceAmounts[invoiceId] || '0');
      if (!isNaN(amount)) {
        total += amount;
      }
    });
    selectedQuotes.forEach((quoteId) => {
      const amount = parseFloat(quoteAmounts[quoteId] || '0');
      if (!isNaN(amount)) {
        total += amount;
      }
    });
    return total;
  }, [selectedInvoices, invoiceAmounts, selectedQuotes, quoteAmounts]);

  // Handle invoice selection toggle
  const handleInvoiceToggle = (invoiceId: string, invoice: OutstandingInvoice) => {
    const newSelected = new Set(selectedInvoices);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
      const newAmounts = { ...invoiceAmounts };
      delete newAmounts[invoiceId];
      setInvoiceAmounts(newAmounts);
    } else {
      newSelected.add(invoiceId);
      // Set default amount to balance due or remaining credit, whichever is smaller
      const balanceRemaining = parseFloat(allocationInfo?.credit_memo.balance_remaining || '0');
      const currentAllocated = totalAllocationAmount;
      const availableCredit = balanceRemaining - currentAllocated;
      const invoiceBalance = parseFloat(invoice.balance_due);
      const defaultAmount = Math.min(invoiceBalance, availableCredit);
      setInvoiceAmounts({
        ...invoiceAmounts,
        [invoiceId]: defaultAmount > 0 ? defaultAmount.toFixed(2) : invoice.balance_due,
      });
    }
    setSelectedInvoices(newSelected);
  };

  // Handle quote selection toggle
  const handleQuoteToggle = (quoteId: string, quote: OutstandingQuote) => {
    const newSelected = new Set(selectedQuotes);
    if (newSelected.has(quoteId)) {
      newSelected.delete(quoteId);
      const newAmounts = { ...quoteAmounts };
      delete newAmounts[quoteId];
      setQuoteAmounts(newAmounts);
    } else {
      newSelected.add(quoteId);
      // Set default amount to balance due or remaining credit, whichever is smaller
      const balanceRemaining = parseFloat(allocationInfo?.credit_memo.balance_remaining || '0');
      const currentAllocated = totalAllocationAmount;
      const availableCredit = balanceRemaining - currentAllocated;
      const quoteBalance = parseFloat(quote.balance_due);
      const defaultAmount = Math.min(quoteBalance, availableCredit);
      setQuoteAmounts({
        ...quoteAmounts,
        [quoteId]: defaultAmount > 0 ? defaultAmount.toFixed(2) : quote.balance_due,
      });
    }
    setSelectedQuotes(newSelected);
  };

  // Handle invoice amount change
  const handleInvoiceAmountChange = (invoiceId: string, value: string) => {
    setInvoiceAmounts({
      ...invoiceAmounts,
      [invoiceId]: value,
    });
  };

  // Handle quote amount change
  const handleQuoteAmountChange = (quoteId: string, value: string) => {
    setQuoteAmounts({
      ...quoteAmounts,
      [quoteId]: value,
    });
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedInvoices(new Set());
    setInvoiceAmounts({});
    setSelectedQuotes(new Set());
    setQuoteAmounts({});
  };

  // Handle save
  const handleSave = async () => {
    if (!id || !allocationInfo) return;

    // Validate
    if (selectedInvoices.size === 0 && selectedQuotes.size === 0) {
      setError('Please select at least one invoice or quote');
      return;
    }

    if (totalAllocationAmount <= 0) {
      setError('Allocation amount must be greater than zero');
      return;
    }

    const balanceRemaining = parseFloat(allocationInfo.credit_memo.balance_remaining);
    if (totalAllocationAmount > balanceRemaining) {
      setError(`Total allocation (${formatAmount(totalAllocationAmount)}) exceeds available credit (${formatAmount(balanceRemaining)})`);
      return;
    }

    setSaving(true);
    setError('');

    // Build allocations
    const allocations = [
      ...Array.from(selectedInvoices).map((invoiceId) => ({
        invoice: invoiceId,
        amount: invoiceAmounts[invoiceId] || '0',
      })),
      ...Array.from(selectedQuotes).map((quoteId) => ({
        quote: quoteId,
        amount: quoteAmounts[quoteId] || '0',
      })),
    ];

    const response = await creditMemoApi.applyCredit(id, { allocations });

    if (response.data) {
      nav(`/credit-memos/${id}`);
    } else if (response.error) {
      setError(response.error);
      setSaving(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!allocationInfo) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
          <div className="h-6 w-px bg-border" />
          <span className="font-medium text-sm">{currentBusiness?.name}</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Credit memo not found'}</p>
            <Button variant="outline" onClick={() => nav('/credit-memos')}>
              Back to Credit Memos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const creditMemo = allocationInfo.credit_memo;
  const balanceRemaining = parseFloat(creditMemo.balance_remaining);
  const totalSelected = selectedInvoices.size + selectedQuotes.size;
  const isOverAllocated = totalAllocationAmount > balanceRemaining;

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col overflow-hidden">
      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-1 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <img src="/owl.svg" alt="InvoiceOwl" className="h-8 w-auto" />
            <div className="h-6 w-px bg-border" />
            <span className="font-medium text-sm">{currentBusiness?.name}</span>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-8 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="max-w-3xl mx-auto space-y-6"
            >
              {/* Title */}
              <h1 className="text-2xl flex items-center gap-2">
                Apply Credit
                <CreditCard className="h-5 w-5 text-amber-500" />
              </h1>

              {/* Credit Memo Info Section */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="space-y-2"
              >
                <Label className="text-muted-foreground">Credit Memo</Label>
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium">{creditMemo.client_name}</p>
                      <p className="text-sm text-muted-foreground">
                        Credit Memo #{creditMemo.credit_memo_number}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      Total Credit
                    </p>
                    <p className="text-lg font-semibold">
                      {formatAmount(creditMemo.total_amount)}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Outstanding Invoices */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <Label className="text-muted-foreground">Select Invoices to Apply Credit</Label>
                  {totalSelected > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">
                        {totalSelected} selected
                      </span>
                      <button
                        type="button"
                        onClick={handleDeselectAll}
                        className="text-sm text-primary hover:underline"
                      >
                        Deselect all
                      </button>
                    </div>
                  )}
                </div>

                {allocationInfo.outstanding_invoices.length === 0 ? (
                  <div className="border rounded-lg p-8 text-center bg-muted/30">
                    <Receipt className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">
                      No outstanding invoices for this client.
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 w-8"></th>
                          <th className="text-left px-3 py-2.5">Invoice</th>
                          <th className="text-left px-3 py-2.5">Status</th>
                          <th className="text-left px-3 py-2.5">Address</th>
                          <th className="text-right px-3 py-2.5">Total</th>
                          <th className="text-right px-3 py-2.5">Balance</th>
                          <th className="text-right px-4 py-2.5 w-36">Apply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocationInfo.outstanding_invoices.map((invoice) => {
                          const isSelected = selectedInvoices.has(invoice.id);
                          const statusConfig = getInvoiceStatusConfig(invoice.status);

                          return (
                            <tr
                              key={invoice.id}
                              className={`border-t transition-colors ${isSelected ? 'bg-amber-500/5' : 'hover:bg-muted/25'}`}
                            >
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleInvoiceToggle(invoice.id, invoice)}
                                />
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  type="button"
                                  onClick={() => nav(`/invoices/${invoice.id}`)}
                                  className="text-primary hover:underline font-medium text-sm"
                                >
                                  #{invoice.invoice_number.replace('INV-', '').replace(/^0+/, '') || invoice.invoice_number}
                                </button>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  Due {invoice.due_date
                                    ? format(new Date(invoice.due_date), 'MMM d, yyyy')
                                    : '—'}
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={statusConfig.textColorClass}>
                                  {statusConfig.label}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                                {invoice.client_address || '—'}
                              </td>
                              <td className="px-3 py-3 text-right text-sm">
                                {formatAmount(invoice.total_amount)}
                              </td>
                              <td className="px-3 py-3 text-right font-medium text-sm">
                                {formatAmount(invoice.balance_due)}
                              </td>
                              <td className="px-4 py-3">
                                <CurrencyInput
                                  value={isSelected ? (invoiceAmounts[invoice.id] || '') : ''}
                                  onChange={(value) => handleInvoiceAmountChange(invoice.id, value)}
                                  disabled={!isSelected}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>

              {/* Outstanding Quote Deposits */}
              {allocationInfo.outstanding_quotes.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="space-y-4"
                >
                  <Label className="text-muted-foreground">Select Quotes with Deposits Due</Label>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted/50">
                        <tr className="text-xs text-muted-foreground">
                          <th className="text-left px-4 py-2.5 w-8"></th>
                          <th className="text-left px-3 py-2.5">Quote</th>
                          <th className="text-left px-3 py-2.5">Status</th>
                          <th className="text-left px-3 py-2.5">Address</th>
                          <th className="text-right px-3 py-2.5">Deposit</th>
                          <th className="text-right px-3 py-2.5">Balance</th>
                          <th className="text-right px-4 py-2.5 w-36">Apply</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allocationInfo.outstanding_quotes.map((quote) => {
                          const isSelected = selectedQuotes.has(quote.id);
                          const statusConfig = getQuoteStatusConfig(quote.status);

                          return (
                            <tr
                              key={quote.id}
                              className={`border-t transition-colors ${isSelected ? 'bg-amber-500/5' : 'hover:bg-muted/25'}`}
                            >
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleQuoteToggle(quote.id, quote)}
                                />
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  type="button"
                                  onClick={() => nav(`/quotes/${quote.id}`)}
                                  className="text-primary hover:underline font-medium text-sm"
                                >
                                  #{quote.quote_number.replace('QUO-', '').replace(/^0+/, '') || quote.quote_number}
                                </button>
                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  Quote Deposit
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={statusConfig.textColorClass}>
                                  {statusConfig.label}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                                {quote.client_address || '—'}
                              </td>
                              <td className="px-3 py-3 text-right text-sm">
                                {formatAmount(quote.deposit)}
                              </td>
                              <td className="px-3 py-3 text-right font-medium text-sm">
                                {formatAmount(quote.balance_due)}
                              </td>
                              <td className="px-4 py-3">
                                <CurrencyInput
                                  value={isSelected ? (quoteAmounts[quote.id] || '') : ''}
                                  onChange={(value) => handleQuoteAmountChange(quote.id, value)}
                                  disabled={!isSelected}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}

              {/* Error Message */}
              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-destructive text-sm"
                >
                  {error}
                </motion.p>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Right Sidebar */}
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', damping: 25 }}
          className="w-72 border-l bg-muted/30 flex flex-col flex-shrink-0"
        >
          {/* Applying Amount Header */}
          <div className="px-5 py-4 border-b">
            <div className="text-sm text-muted-foreground">Applying</div>
            <div className={`text-2xl font-semibold ${isOverAllocated ? 'text-red-500' : ''}`}>
              {formatAmount(totalAllocationAmount)}
            </div>
            {isOverAllocated && (
              <p className="text-xs text-red-500 mt-1">
                Exceeds available by {formatAmount(totalAllocationAmount - balanceRemaining)}
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 pt-4 space-y-5">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Credit Details
            </h2>

            {/* Credit Memo Info */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Credit</span>
                <span className="font-medium">{formatAmount(creditMemo.total_amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Already Applied</span>
                <span className="font-medium">{formatAmount(creditMemo.amount_allocated)}</span>
              </div>
              <div className="h-px bg-border" />
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Available</span>
                <span className="font-semibold text-green-600">{formatAmount(balanceRemaining)}</span>
              </div>
            </div>

            {/* After This Allocation */}
            {totalAllocationAmount > 0 && !isOverAllocated && (
              <div className="space-y-3 pt-2">
                <div className="h-px bg-border" />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">After Allocation</span>
                  <span className="font-semibold">
                    {formatAmount(balanceRemaining - totalAllocationAmount)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Fixed action buttons at bottom */}
          <div className="p-4 border-t bg-background space-y-3">
            <Button
              onClick={handleSave}
              disabled={saving || totalSelected === 0 || totalAllocationAmount <= 0 || isOverAllocated}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              {saving ? 'Applying...' : 'Apply Credit'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              disabled={saving}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export function ApplyCreditMemo() {
  return <ApplyCreditMemoContent />;
}
